import type { DeviceBatteryMeasurement, DeviceGroupMemberMeasurements, DeviceGroupMemberRole, DeviceGroupMemberStatusItem } from '@/shared/api/device-map-marker-client';
import { resolveBatteryDisplayPercent, type BatteryLike } from '@/shared/helper/battery-status';

export type GroupMemberEventLevel = 'alert' | 'warning' | 'normal' | 'unknown';
export type DeviceStateTone = 'alert' | 'warning' | 'normal' | 'off' | 'unknown';
export type DeviceGroupMetricItem = {
  key: string;
  label: string;
  layout?: 'wide';
  value: string;
};
export type DeviceMetricSource = Pick<
  DeviceGroupMemberStatusItem,
  | 'batteries'
  | 'capability_key'
  | 'control_response'
  | 'gps_status'
  | 'gps_valid'
  | 'gpsStatus'
  | 'gpsValid'
  | 'is_cctv'
  | 'lora_type'
  | 'lora_type_name'
  | 'measurements'
  | 'product_name'
  | 'sensor_type'
  | 'sensor_type_name'
>;

const GROUP_COUNTER_CHILD_SENSOR_TYPE_CODES = new Set([4, 7, 8, 18]);
const GROUP_COUNTER_CHILD_SENSOR_TYPE_NAMES = new Set(['CK', 'SJ', 'SJ_MINI', 'SJ_WATER']);
const TRACKER_SENSOR_TYPE_CODES = new Set([17]);
const TRACKER_SENSOR_TYPE_NAMES = new Set(['TRACKER']);
const GROUP_MEMBER_ROLES = new Set<DeviceGroupMemberRole>(['MAIN', 'COUNTER_CHILD', 'TRACKER', 'GROUP_MEMBER']);
const MOVEMENT_OFF_REASON_LABELS: Record<string, string> = {
  '00': '진출',
  '01': '사용자OFF',
  '02': '저전력OFF',
};
const MOVEMENT_ENTRY_TOKENS = new Set(['11', 'ENTRY', '진입']);
const MOVEMENT_EXIT_TOKENS = new Set(['10', 'EXIT', '진출', '사용자OFF', '저전력OFF']);
const GROUP_MEMBER_WARNING_REASON_IGNORED_TOKENS = new Set(['정상', 'NORMAL', 'ON', '경고', '주의', 'ALERT', 'WARNING']);

/**
 * 상태 문자열을 대문자 코드로 정규화합니다.
 */
export function normalizeStatusCode(value: string | null | undefined): string {
  return (value ?? '').trim().toUpperCase();
}

/**
 * 연결 장비 row가 위험알리미 그룹 카운터 대상 자식 장비인지 판별합니다.
 */
export function isGroupCounterChildMember(item: DeviceGroupMemberStatusItem): boolean {
  if (typeof item.is_group_counter_child === 'boolean') {
    return item.is_group_counter_child;
  }

  const sensorType = Number(item.sensor_type);
  if (Number.isInteger(sensorType) && GROUP_COUNTER_CHILD_SENSOR_TYPE_CODES.has(sensorType)) {
    return true;
  }

  return GROUP_COUNTER_CHILD_SENSOR_TYPE_NAMES.has(normalizeStatusCode(item.sensor_type_name));
}

/**
 * 연결 장비 row의 트리 표시 역할을 정규화합니다.
 */
export function resolveGroupMemberRole(item: DeviceGroupMemberStatusItem): DeviceGroupMemberRole {
  const role = normalizeStatusCode(item.group_member_role) as DeviceGroupMemberRole;
  if (GROUP_MEMBER_ROLES.has(role)) {
    return role;
  }
  if (item.is_group_main_device) {
    return 'MAIN';
  }
  if (isGroupCounterChildMember(item)) {
    return 'COUNTER_CHILD';
  }

  const sensorType = Number(item.sensor_type);
  if (Number.isInteger(sensorType) && TRACKER_SENSOR_TYPE_CODES.has(sensorType)) {
    return 'TRACKER';
  }
  if (TRACKER_SENSOR_TYPE_NAMES.has(normalizeStatusCode(item.sensor_type_name))) {
    return 'TRACKER';
  }
  return 'GROUP_MEMBER';
}

/**
 * 연결 장비 역할 코드를 패널 표시 문구로 변환합니다.
 */
export function formatGroupMemberRole(item: DeviceGroupMemberStatusItem): string {
  const role = resolveGroupMemberRole(item);
  if (role === 'MAIN') {
    return '대표';
  }
  if (role === 'COUNTER_CHILD') {
    return '카운터';
  }
  if (role === 'TRACKER') {
    return '그룹';
  }
  return '그룹';
}

/**
 * 그룹 장비 row의 GPS 미수신 표시 문구를 계산합니다.
 */
export function formatGroupMemberGpsNoFixLabel(item: DeviceMetricSource): string | null {
  const status = normalizeStatusCode(item.gps_status ?? item.gpsStatus);
  const gpsValid = item.gps_valid ?? item.gpsValid;
  if (!(gpsValid === false || status === 'NO_FIX')) {
    return null;
  }
  return '수신 실패';
}

/**
 * 그룹 장비 row의 최신 GPS 수신 상태를 사용자 표시 문구로 변환합니다.
 */
function formatGroupMemberGpsReceptionLabel(item: DeviceMetricSource): string | null {
  const noFixLabel = formatGroupMemberGpsNoFixLabel(item);
  if (noFixLabel) {
    return noFixLabel;
  }

  const status = normalizeStatusCode(item.gps_status ?? item.gpsStatus);
  const gpsValid = item.gps_valid ?? item.gpsValid;
  if (gpsValid === true || status === 'FIX') {
    return '수신 중';
  }

  return null;
}

/**
 * 장비의 최신 SACON 상태를 표시 위험도 레벨로 변환합니다.
 */
export function resolveGroupMemberEventLevel(item: DeviceGroupMemberStatusItem): GroupMemberEventLevel {
  const state = normalizeStatusCode(item.sacon_state);
  if (state === 'ALERT') {
    return 'alert';
  }
  if (state === 'WARNING') {
    return 'warning';
  }
  if (state === 'ERROR') {
    return 'unknown';
  }
  if (state === 'NORMAL' || state === 'MEASURE' || item.sensor_state_name || item.sensor_state) {
    return 'normal';
  }
  return 'unknown';
}

/**
 * 장비 운용 상태 코드를 사용자 표시명으로 변환합니다.
 */
export function formatDeviceState(value: string | null | undefined): string {
  const normalizedValue = normalizeStatusCode(value);
  if (normalizedValue === 'NORMAL') {
    return 'ON';
  }
  if (normalizedValue === 'OFF') {
    return 'OFF';
  }
  if (normalizedValue === 'ALERT') {
    return '경고';
  }
  if (normalizedValue === 'WARNING') {
    return '주의';
  }
  if (normalizedValue === 'INVISUAL') {
    return '숨김';
  }
  return '상태 없음';
}

/**
 * 장비 운용 상태 코드를 UI 색상 토큰으로 변환합니다.
 */
export function resolveDeviceStateTone(value: string | null | undefined): DeviceStateTone {
  const normalizedValue = normalizeStatusCode(value);
  if (normalizedValue === 'OFF' || normalizedValue === 'INVISUAL') {
    return 'off';
  }
  if (normalizedValue === 'ALERT') {
    return 'alert';
  }
  if (normalizedValue === 'WARNING') {
    return 'warning';
  }
  if (normalizedValue === 'NORMAL') {
    return 'normal';
  }
  return 'unknown';
}

/**
 * 안전지키미 이동 이벤트를 연결 상태 표시용 장비 상태 코드로 변환합니다.
 */
function resolveGroupMemberMovementStateCode(item: DeviceGroupMemberStatusItem): string | null {
  if (normalizeStatusCode(item.movement_display_label) === '경고 해제') return null;
  const movementTokens = [item.movement_event, item.movement_event_label, item.movement_display_label, item.sensor_state, item.sensor_state_name].map(value =>
    normalizeStatusCode(value)
  );

  if (movementTokens.some(token => MOVEMENT_EXIT_TOKENS.has(token)) || formatMovementOffReasonName(item) !== null) {
    return 'OFF';
  }
  if (movementTokens.some(token => MOVEMENT_ENTRY_TOKENS.has(token))) {
    return 'NORMAL';
  }

  return null;
}

/**
 * 연결장비 표시용 연결 상태를 장비 상태 코드로 변환합니다.
 */
export function resolveGroupMemberConnectionStateCode(item: DeviceGroupMemberStatusItem): string | null {
  const connectionState = normalizeStatusCode(item.connection_state);
  if (connectionState === 'OFF') {
    return 'OFF';
  }

  const deviceState = normalizeStatusCode(item.device_state);
  if (deviceState === 'OFF' || deviceState === 'INVISUAL') {
    return deviceState;
  }
  if (deviceState === 'ALERT' || deviceState === 'WARNING') {
    return deviceState;
  }

  const eventLevel = resolveGroupMemberEventLevel(item);
  if (eventLevel === 'alert') {
    return 'ALERT';
  }
  if (eventLevel === 'warning') {
    return 'WARNING';
  }
  if (connectionState === 'ON') {
    return 'NORMAL';
  }

  const movementState = resolveGroupMemberMovementStateCode(item);
  if (movementState === 'OFF') {
    return 'OFF';
  }
  if (movementState === 'NORMAL') {
    return 'NORMAL';
  }
  return item.device_state ?? null;
}

/**
 * VWorld 지도 연결장비 배지에서 실시간 장비 상태를 표시합니다.
 */
export function resolveVWorldConnectedDeviceStatusCode(item: DeviceGroupMemberStatusItem): string | null {
  return resolveGroupMemberConnectionStateCode(item);
}

/**
 * VWorld 지도 연결장비 배지 상태를 사용자 표시 문구로 변환합니다.
 */
export function formatVWorldConnectedDeviceStatus(item: DeviceGroupMemberStatusItem): string {
  return formatGroupMemberConnectionState(item);
}

/**
 * 센서 상태명 한 항목에서 일반 상태·심각도 문구를 제거하고 실제 경고 사유만 반환합니다.
 */
function normalizeGroupMemberWarningReason(value: string): string | null {
  const normalizedValue = value.trim();
  if (!normalizedValue || GROUP_MEMBER_WARNING_REASON_IGNORED_TOKENS.has(normalizeStatusCode(normalizedValue))) {
    return null;
  }

  const reason = normalizedValue.replace(/\s*(?:경고|주의)$/u, '').trim();
  if (!reason || GROUP_MEMBER_WARNING_REASON_IGNORED_TOKENS.has(normalizeStatusCode(reason))) {
    return null;
  }
  return reason;
}

/**
 * 연결 장비의 ALERT/WARNING 상태와 최신 센서·배터리 값에서 화면 표시용 경고 사유를 계산합니다.
 */
export function resolveGroupMemberWarningReason(item: DeviceGroupMemberStatusItem): string | null {
  const stateCode = resolveGroupMemberConnectionStateCode(item);
  if (stateCode !== 'ALERT' && stateCode !== 'WARNING') {
    return null;
  }

  const sensorReason = (item.sensor_state_name ?? '')
    .split(',')
    .map(normalizeGroupMemberWarningReason)
    .filter((reason): reason is string => reason !== null)
    .join(', ');
  if (sensorReason) {
    return sensorReason;
  }

  // 센서 사유가 없거나 정상·일반 심각도 문구이면 기존 배터리 저전압 판정을 사용합니다.
  const battery = item.batt;
  if (battery && typeof battery === 'object') {
    const batteryRecord = battery as Record<string, unknown>;
    const batteryStatus = String(batteryRecord.status ?? batteryRecord.state ?? '')
      .trim()
      .toLowerCase();
    if (batteryRecord.isLowBattery === true || batteryRecord.is_low_battery === true || ['low', 'alert', 'warning'].includes(batteryStatus)) {
      return '저전압';
    }
  }

  return null;
}

/**
 * 대표 장비 행에서는 자식 집계 상태가 아닌 대표 장비 자체 SACON 상태를 표시합니다.
 */
export function resolveGroupParentDisplayStateCode(item: DeviceGroupMemberStatusItem): string | null {
  const deviceState = normalizeStatusCode(item.device_state);
  if (deviceState === 'OFF' || deviceState === 'INVISUAL') {
    return deviceState;
  }

  const eventLevel = resolveGroupMemberEventLevel(item);
  if (eventLevel === 'alert') {
    return 'ALERT';
  }
  if (eventLevel === 'warning') {
    return 'WARNING';
  }
  if (eventLevel === 'normal') {
    return 'NORMAL';
  }
  return item.device_state ?? null;
}

/**
 * 연결장비 표시용 ON/OFF 문구를 계산합니다.
 */
export function formatGroupMemberConnectionState(item: DeviceGroupMemberStatusItem): string {
  const stateCode = resolveGroupMemberConnectionStateCode(item);
  const normalizedStateCode = normalizeStatusCode(stateCode);
  if (normalizedStateCode === 'NORMAL') {
    return 'ON';
  }
  if (normalizedStateCode === 'OFF') {
    return 'OFF';
  }
  return formatDeviceState(stateCode);
}

/**
 * OFF 장비 숨김 설정에서 연결장비 row를 표시할지 판정합니다.
 */
export function shouldDisplayGroupMemberByOfflineSetting(item: DeviceGroupMemberStatusItem, showOfflineDevices: boolean): boolean {
  // INVISUAL은 연결 상태가 아니라 지도 가시성 상태이므로 OFF 표시 설정과 무관하게 숨깁니다.
  if (normalizeStatusCode(item.device_state) === 'INVISUAL') {
    return false;
  }

  const stateCode = normalizeStatusCode(resolveGroupMemberConnectionStateCode(item));
  if (showOfflineDevices) {
    return true;
  }

  return stateCode !== 'OFF';
}

/**
 * 연결장비 row의 상태 배지 톤을 계산합니다.
 */
export function resolveGroupMemberConnectionStateTone(item: DeviceGroupMemberStatusItem): DeviceStateTone {
  return resolveDeviceStateTone(resolveGroupMemberConnectionStateCode(item));
}

/**
 * 진출 잔존 row에 표시할 진출 사유명을 반환합니다.
 */
export function formatMovementOffReasonName(item: Pick<DeviceGroupMemberStatusItem, 'movement_off_reason' | 'movement_off_reason_name' | 'movement_display_label'>): string | null {
  const reasonName = item.movement_off_reason_name?.trim();
  if (reasonName && reasonName.length > 0) {
    return reasonName;
  }

  const reasonCode = item.movement_off_reason?.trim().toUpperCase().replace(/^0X/, '').padStart(2, '0');
  if (reasonCode && MOVEMENT_OFF_REASON_LABELS[reasonCode]) {
    return MOVEMENT_OFF_REASON_LABELS[reasonCode];
  }

  const displayLabel = item.movement_display_label?.trim();
  return displayLabel === '사용자OFF' || displayLabel === '저전력OFF' ? displayLabel : null;
}

/**
 * 최종 화면 상태가 실제 OFF인 연결장비의 현재 꺼짐 사유만 반환합니다.
 */
export function formatCurrentMovementOffReasonName(item: DeviceGroupMemberStatusItem): string | null {
  if (normalizeStatusCode(item.device_state) === 'INVISUAL') {
    return null;
  }
  if (normalizeStatusCode(resolveGroupMemberConnectionStateCode(item)) !== 'OFF') {
    return null;
  }
  return formatMovementOffReasonName(item);
}

/**
 * 장비 정렬 우선순위를 계산합니다.
 */
export function resolveGroupMemberSortPriority(item: DeviceGroupMemberStatusItem): number {
  const level = resolveGroupMemberEventLevel(item);
  if (level === 'alert') {
    return 0;
  }
  if (level === 'warning') {
    return 1;
  }
  if (level === 'normal') {
    return 2;
  }
  return 3;
}

/**
 * ISO 일시 문자열을 한국어 표시 문자열로 변환합니다.
 */
export function formatGroupStatusDateTime(value: string | null | undefined): string {
  if (!value) {
    return '-';
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  const dateText = parsedDate.toLocaleDateString('ko-KR');
  const timeText = [parsedDate.getHours(), parsedDate.getMinutes(), parsedDate.getSeconds()].map(part => part.toString().padStart(2, '0')).join(':');
  return `${dateText} ${timeText}`;
}

/**
 * 배터리 정보를 패널 표시 문자열로 변환합니다.
 */
export function formatGroupMemberBattery(value: DeviceGroupMemberStatusItem['batt']): string {
  const percent = resolveBatteryDisplayPercent(value as BatteryLike | null | undefined);
  return percent === undefined ? '-' : `${percent}%`;
}

/**
 * 갱신 시각 후보 중 유효한 가장 최근 일시 문자열을 반환합니다.
 */
function resolveLatestValidUpdatedAt(values: Array<string | null | undefined>): string | null {
  let latestValue: string | null = null;
  let latestTime = Number.NEGATIVE_INFINITY;

  for (const value of values) {
    if (typeof value !== 'string' || value.trim().length === 0) {
      continue;
    }

    // 잘못된 날짜 문자열은 갱신 시각 비교 대상에서 제외합니다.
    const time = new Date(value).getTime();
    if (!Number.isFinite(time) || time <= latestTime) {
      continue;
    }

    latestValue = value;
    latestTime = time;
  }

  return latestValue;
}

/**
 * 단일 장비의 SACON·장비 정보 갱신 시각 중 유효한 최신값을 계산합니다.
 */
export function resolveGroupMemberUpdatedAt(item: DeviceGroupMemberStatusItem): string | null {
  return resolveLatestValidUpdatedAt([item.sacon_updated_at, item.device_updated_at]);
}

/**
 * 그룹 내 장비 목록에서 가장 최근 갱신 시각을 계산합니다.
 */
export function resolveGroupUpdatedAt(items: DeviceGroupMemberStatusItem[]): string | null {
  return resolveLatestValidUpdatedAt(items.map(resolveGroupMemberUpdatedAt));
}

/**
 * 숫자 측정값을 표시 문자열로 변환합니다.
 */
function formatMeasurementValue(value: number | null | undefined, unit: string, fractionDigits = 1): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '-';
  }

  return `${value.toFixed(fractionDigits)}${unit}`;
}

/**
 * 숫자 측정값 원본 정밀도를 유지해 표시 문자열로 변환합니다.
 */
function formatMeasurementRawValue(value: number | null | undefined, unit: string): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '-';
  }

  return `${value.toString()}${unit}`;
}

/**
 * 대여자 이름을 장비명 옆 표시용 문자열로 정규화합니다.
 */
export function formatBorrowerName(value: string | null | undefined): string | null {
  const borrowerName = value?.trim();
  return borrowerName && borrowerName.length > 0 ? borrowerName : null;
}

/**
 * 풍향 문자열을 표시 문자열로 변환합니다.
 */
function formatWindDirection(value: string | null | undefined): string {
  return value?.trim() || '-';
}

/**
 * 전원제어 상태 코드와 표시명을 화면용 한글 문자열로 변환합니다.
 */
function formatPowerControlState(code: number | null | undefined, name: string | null | undefined, labels: Record<number, string>): string {
  const normalizedName = name?.trim();
  if (normalizedName && normalizeStatusCode(normalizedName) !== 'UNKNOWN') {
    return normalizedName;
  }
  if (typeof code === 'number' && Number.isInteger(code)) {
    return labels[code] ?? '미확인';
  }
  return '미확인';
}

/**
 * 전원제어 타이머 대상 니블을 사용자 표시 문구로 변환합니다.
 */
function formatPowerControlSwitch(code: number | null | undefined): string {
  if (code === 0) return '꺼짐';
  if (code === 1) return '켜짐';
  return '미확인';
}

/**
 * GPS 전원 제어값을 수신 상태 표시 문구로 변환합니다.
 */
function formatPowerControlGpsReception(code: number | null | undefined): string {
  if (code === 0) return '수신 실패';
  if (code === 1) return '수신 중';
  return '미확인';
}

/**
 * 라우터·GPS·표시등의 타이머 대상 값을 하나의 예약 전원 연동 상태로 통합합니다.
 */
function formatPowerTimerLink(values: Array<number | null | undefined>): string | null {
  const timerValues = values.filter((value): value is number => value != null);
  if (timerValues.length === 0) {
    return null;
  }

  // 하나라도 프로토콜 허용값을 벗어나면 잘못된 상태를 ON/OFF로 오인하지 않습니다.
  if (timerValues.some(value => value !== 0 && value !== 1)) {
    return '미확인';
  }

  return timerValues.every(value => value === timerValues[0]) ? formatPowerControlSwitch(timerValues[0]) : '혼합';
}

/**
 * 제품 센서 타입을 enum 이름으로 정규화합니다.
 */
export function resolveSensorTypeName(item: DeviceMetricSource): string {
  const normalizedName = normalizeStatusCode(item.sensor_type_name);
  if (normalizedName) {
    return normalizedName;
  }

  switch (item.sensor_type) {
    case 11:
      return 'DUST';
    case 12:
      return 'GAS';
    case 13:
      return 'SOUND';
    case 14:
      return 'TEMP_HUM';
    case 15:
      return 'WIND';
    case 19:
      return 'MBS';
    case 20:
      return 'HEAT';
    case 30:
      return 'SJ_WATER_STRUCTURE';
    default:
      return '';
  }
}

/**
 * 공통 장비 payload에서 frontend 표시 capability key를 결정합니다.
 */
export function resolveDeviceCapabilityName(item: DeviceMetricSource): string {
  return normalizeStatusCode(item.capability_key) || resolveSensorTypeName(item) || normalizeStatusCode(item.lora_type_name) || (item.lora_type === 4 ? 'A1' : '');
}

function compactMetricItems(items: DeviceGroupMetricItem[]): DeviceGroupMetricItem[] {
  return items.filter(item => item.value !== '-');
}

type MeasurementMetricDefinition = {
  fractionDigits?: number;
  key: keyof DeviceGroupMemberMeasurements;
  label: string;
  raw?: boolean;
  unit?: string;
  valueType?: 'text';
};

type CapabilityDisplayDefinition = {
  includeBatteries?: boolean;
  includeControlResponse?: boolean;
  includeGps?: boolean;
  measurements?: readonly MeasurementMetricDefinition[];
  powerControl?: boolean;
};

const CAPABILITY_DISPLAY_REGISTRY: Readonly<Record<string, CapabilityDisplayDefinition>> = {
  A1: { includeControlResponse: true },
  DUST: {
    includeGps: true,
    measurements: [
      { key: 'pm1_0', label: 'PM1.0', unit: '㎍/m³', fractionDigits: 0 },
      { key: 'pm2_5', label: 'PM2.5', unit: '㎍/m³', fractionDigits: 0 },
      { key: 'pm10', label: 'PM10', unit: '㎍/m³', fractionDigits: 0 },
    ],
  },
  DX: {
    includeGps: true,
    measurements: [
      { key: 'xdeg', label: 'X축 각도', unit: '°', raw: true },
      { key: 'ydeg', label: 'Y축 각도', unit: '°', raw: true },
      { key: 'zdeg', label: 'Z축 각도', unit: '°', raw: true },
    ],
  },
  FIRE: { measurements: [{ key: 'tp', label: '현재 온도', unit: '°C' }] },
  G1: {
    includeGps: true,
    measurements: [
      { key: 'xdeg', label: 'X축 각도', unit: '°', raw: true },
      { key: 'ydeg', label: 'Y축 각도', unit: '°', raw: true },
      { key: 'zdeg', label: 'Z축 각도', unit: '°', raw: true },
    ],
  },
  G2: {
    includeGps: true,
    measurements: [
      { key: 'xdeg', label: 'X축 각도', unit: '°', raw: true },
      { key: 'ydeg', label: 'Y축 각도', unit: '°', raw: true },
      { key: 'zdeg', label: 'Z축 각도', unit: '°', raw: true },
    ],
  },
  GAS: {
    includeBatteries: true,
    includeGps: true,
    measurements: [
      { key: 'co', label: 'CO', unit: 'ppm', fractionDigits: 0 },
      { key: 'o2', label: 'O2', unit: '%', fractionDigits: 1 },
      { key: 'h2s', label: 'H2S', unit: 'ppm', fractionDigits: 1 },
      { key: 'exp', label: 'EXP', unit: '%LEL', fractionDigits: 0 },
      { key: 'tvoc', label: 'TVOC', unit: 'ppm', fractionDigits: 3 },
      { key: 'co2', label: 'CO2', unit: 'ppm', fractionDigits: 0 },
      { key: 'tp', label: '온도', unit: '°C' },
      { key: 'hm', label: '습도', unit: '%', fractionDigits: 0 },
    ],
  },
  GKH: { measurements: [{ key: 'zdeg', label: 'Z축 각도', unit: '°', raw: true }] },
  HEAT: {
    includeGps: true,
    measurements: [
      { key: 'appr_tp', label: '체감', unit: '°C' },
      { key: 'tp', label: '온도', unit: '°C' },
      { key: 'hm', label: '습도', unit: '%', fractionDigits: 0 },
    ],
  },
  MBS: { powerControl: true },
  PCU: { powerControl: true },
  SOUND: {
    measurements: [
      { key: 'dba', label: '소음', unit: 'dBA' },
      { key: 'dbv', label: '진동', unit: 'dBV' },
    ],
  },
  TEMP_HUM: {
    includeGps: true,
    measurements: [
      { key: 'tp', label: '온도', unit: '°C' },
      { key: 'hm', label: '습도', unit: '%', fractionDigits: 0 },
    ],
  },
  WIND: {
    includeGps: true,
    measurements: [
      { key: 'wind_dir', label: '풍향', valueType: 'text' },
      { key: 'wind_deg', label: '풍향각', unit: '°', raw: true },
      { key: 'wind_spd', label: '풍속', unit: 'm/s', fractionDigits: 2 },
    ],
  },
};

const CONFINED_SPACE_METRICS: readonly MeasurementMetricDefinition[] = [
  { key: 'tp', label: '온도', unit: '°C' },
  { key: 'hm', label: '습도', unit: '%', fractionDigits: 0 },
  { key: 'co', label: 'CO', unit: 'ppm', fractionDigits: 0 },
  { key: 'o2', label: 'O2', unit: '%', fractionDigits: 1 },
  { key: 'h2s', label: 'H2S', unit: 'ppm', fractionDigits: 1 },
  { key: 'exp', label: 'EXP', unit: '%LEL', fractionDigits: 0 },
  { key: 'tvoc', label: 'TVOC', unit: 'ppm', fractionDigits: 3 },
  { key: 'co2', label: 'CO2', unit: 'ppm', fractionDigits: 0 },
];

function buildMeasurementMetricItems(measurements: DeviceGroupMemberMeasurements, definitions: readonly MeasurementMetricDefinition[]): DeviceGroupMetricItem[] {
  return definitions.map(definition => {
    const value = measurements[definition.key];
    if (definition.valueType === 'text') {
      return { key: definition.key, label: definition.label, value: formatWindDirection(typeof value === 'string' ? value : null) };
    }
    const numericValue = typeof value === 'number' ? value : null;
    return {
      key: definition.key,
      label: definition.label,
      value: definition.raw
        ? formatMeasurementRawValue(numericValue, definition.unit ?? '')
        : formatMeasurementValue(numericValue, definition.unit ?? '', definition.fractionDigits),
    };
  });
}

function formatBatteryMeasurement(battery: DeviceBatteryMeasurement): string | null {
  const parts = [
    typeof battery.percent === 'number' && Number.isFinite(battery.percent) ? `${battery.percent.toLocaleString('ko-KR')}%` : null,
    typeof battery.volt === 'number' && Number.isFinite(battery.volt) ? `${battery.volt.toLocaleString('ko-KR')}V` : null,
    battery.status?.trim() || null,
  ].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(' · ') : null;
}

function buildPowerControlMetricItems(measurements: DeviceGroupMemberMeasurements, gpsMetric: string | null): DeviceGroupMetricItem[] {
  const powerMetrics: DeviceGroupMetricItem[] = [];
  if (measurements.power_operation_state != null || measurements.power_operation_state_name) {
    powerMetrics.push({
      key: 'power_operation_state',
      label: '전원 상태',
      value: formatPowerControlState(measurements.power_operation_state, measurements.power_operation_state_name, { 0: '정상', 1: '저전압', 2: '절전' }),
    });
  }
  if (measurements.power_charging_state != null || measurements.power_charging_state_name) {
    powerMetrics.push({
      key: 'power_charging_state',
      label: '충전 상태',
      value: formatPowerControlState(measurements.power_charging_state, measurements.power_charging_state_name, { 0: '대기', 1: '충전 중', 2: '충전 완료' }),
    });
  }
  const timerLink = formatPowerTimerLink([measurements.router_timer_control, measurements.gps_timer_control, measurements.indicator_timer_control]);
  if (timerLink) powerMetrics.push({ key: 'power_timer_link', label: '예약 전원 연동', layout: 'wide', value: timerLink });
  if (measurements.power_off_reason != null || measurements.power_off_reason_name) {
    powerMetrics.push({
      key: 'power_off_reason',
      label: '종료 사유',
      layout: 'wide',
      value: measurements.power_off_reason_name?.trim() || String(measurements.power_off_reason),
    });
  }
  powerMetrics.push(
    { key: 'tp', label: '온도', value: formatMeasurementValue(measurements.tp, '°C', 1) },
    { key: 'hm', label: '습도', value: formatMeasurementValue(measurements.hm, '%', 0) }
  );
  const powerGpsMetric = gpsMetric ?? (measurements.gps_on_off != null ? formatPowerControlGpsReception(measurements.gps_on_off) : null);
  return compactMetricItems(powerGpsMetric ? [{ key: 'gps_status', label: 'GPS', value: powerGpsMetric }, ...powerMetrics] : powerMetrics);
}

/**
 * 장비 제품 특성에 맞는 측정 항목만 고정 순서로 구성합니다.
 */
export function resolveGroupMemberMetricItems(item: DeviceMetricSource): DeviceGroupMetricItem[] {
  if (item.is_cctv === true) return [];

  const capability = resolveDeviceCapabilityName(item);
  const measurements = item.measurements ?? {};
  const gpsMetric = formatGroupMemberGpsReceptionLabel(item);
  const definition = capability === 'SJ' && item.product_name?.includes('밀폐') ? { measurements: CONFINED_SPACE_METRICS } : CAPABILITY_DISPLAY_REGISTRY[capability];
  if (!definition) return [];
  if (definition.powerControl) return buildPowerControlMetricItems(measurements, gpsMetric);

  const metrics = buildMeasurementMetricItems(measurements, definition.measurements ?? []);
  if (definition.includeGps && gpsMetric) metrics.unshift({ key: 'gps_status', label: 'GPS', value: gpsMetric });
  if (definition.includeBatteries) {
    for (const battery of item.batteries ?? []) {
      const value = formatBatteryMeasurement(battery);
      if (value) metrics.push({ key: `battery:${battery.name}`, label: battery.name, layout: 'wide', value });
    }
  }
  if (definition.includeControlResponse && item.control_response) {
    const response = [item.control_response.group, item.control_response.value]
      .map(value => value?.trim())
      .filter((value): value is string => Boolean(value))
      .join(' · ');
    if (response) metrics.push({ key: 'control_response', label: '제어 응답', layout: 'wide', value: response });
  }
  return compactMetricItems(metrics);
}

/**
 * 장비 목록을 대표 여부, 위험도, 장비명 기준으로 정렬합니다.
 */
export function sortGroupDeviceList(items: DeviceGroupMemberStatusItem[]): DeviceGroupMemberStatusItem[] {
  return [...items].sort((left, right) => {
    if (left.is_group_main_device !== right.is_group_main_device) {
      return left.is_group_main_device ? -1 : 1;
    }

    const priorityDiff = resolveGroupMemberSortPriority(left) - resolveGroupMemberSortPriority(right);
    if (priorityDiff !== 0) {
      return priorityDiff;
    }
    return left.device_name.localeCompare(right.device_name, 'ko-KR');
  });
}

/**
 * 대표 장비와 연결 장비를 중복 없이 하나의 장비 리스트로 합칩니다.
 */
export function buildGroupDeviceList(parent: DeviceGroupMemberStatusItem | null, children: DeviceGroupMemberStatusItem[]): DeviceGroupMemberStatusItem[] {
  const uniqueItems = new Map<string, DeviceGroupMemberStatusItem>();
  if (parent) {
    uniqueItems.set(parent.uid, parent);
  }
  for (const child of children) {
    uniqueItems.set(child.uid, child);
  }
  return sortGroupDeviceList([...uniqueItems.values()]);
}
